import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import logoBase64 from '../assets/logo.png';

// Convert imported image URL to base64 for jsPDF
let cachedLogoBase64 = null;
async function getLogoBase64() {
  if (cachedLogoBase64) return cachedLogoBase64;
  try {
    const response = await fetch(logoBase64);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        cachedLogoBase64 = reader.result;
        resolve(cachedLogoBase64);
      };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

/**
 * Generate an NDIS invoice PDF
 */
export async function generateInvoicePDF(invoice, settings) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Add logo (top-left, subtle)
  try {
    const logo = await getLogoBase64();
    if (logo) {
      doc.addImage(logo, 'PNG', 15, 8, 12, 12);
    }
  } catch (e) { /* skip logo if it fails */ }

  // "Generated for free" watermark next to logo
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Generated for free by', 29, 13);
  doc.text('easyinvoice.net.au', 29, 17);
  doc.setTextColor(0, 0, 0);
  
  let yPos = 20;
  
  // Header - Business Info
  doc.setFontSize(10);
  doc.text(settings.businessName, pageWidth - 15, yPos, { align: 'right' });
  yPos += 5;
  doc.text(`${settings.address}, ${settings.suburb}`, pageWidth - 15, yPos, { align: 'right' });
  yPos += 5;
  doc.text(`${settings.state} ${settings.postcode}, ${settings.country || 'Australia'}`, pageWidth - 15, yPos, { align: 'right' });
  yPos += 5;
  if (settings.phone) {
    doc.text(settings.phone, pageWidth - 15, yPos, { align: 'right' });
    yPos += 5;
  }
  if (settings.email) {
    doc.text(settings.email, pageWidth - 15, yPos, { align: 'right' });
    yPos += 5;
  }
  doc.text(`ABN: ${settings.abn}`, pageWidth - 15, yPos, { align: 'right' });
  
  // Title
  yPos = 50;
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('TAX INVOICE', 15, yPos);
  
  // Client Info Section
  yPos += 15;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  
  // Check if plan managed
  if (invoice.client.fundingType === 'plan-managed') {
    // Attention To section (name on same line)
    doc.text(`Attention To: ${invoice.client.planManagerName}`, 15, yPos);
    yPos += 5;
    doc.setFont(undefined, 'normal');
    
    if (invoice.client.planManagerOrganization) {
      doc.text(invoice.client.planManagerOrganization, 15, yPos);
      yPos += 5;
    }
    
    if (invoice.client.planManagerEmail) {
      doc.text(invoice.client.planManagerEmail, 15, yPos);
      yPos += 5;
    }
    
    // Regarding Client section
    yPos += 3;
    doc.setFont(undefined, 'bold');
    doc.text(`Regarding Client: ${invoice.client.name}`, 15, yPos);
    yPos += 5;
  } else {
    // Self-managed - Attention To participant (name on same line)
    doc.text(`Attention To: ${invoice.client.name}`, 15, yPos);
    yPos += 5;
    doc.setFont(undefined, 'normal');
  }
  
  // Common client details
  doc.setFont(undefined, 'normal');
  doc.text(`NDIS: ${invoice.client.ndisNumber}`, 15, yPos);
  yPos += 5;
  
  if (invoice.client.dob) {
    doc.text(`DOB: ${format(new Date(invoice.client.dob), 'dd/MM/yyyy')}`, 15, yPos);
    yPos += 5;
  }
  
  if (invoice.client.jobDescription) {
    const jobLines = doc.splitTextToSize(`Job Description: ${invoice.client.jobDescription}`, 100);
    jobLines.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 5;
    });
  }
  
  // Invoice Details (right side)
  yPos = 65;

  // Safe date formatting for invoice date
  let invoiceDateStr = 'Date Not Available';
  try {
    if (invoice.date) {
      const invoiceDate = invoice.date instanceof Date ? invoice.date : new Date(invoice.date);
      if (!isNaN(invoiceDate.getTime()) && invoiceDate.getTime() !== 0) {
        invoiceDateStr = format(invoiceDate, 'dd MMM yyyy');
      }
    }
  } catch (error) {
    // Silently handled
  }

  doc.text(`Invoice Date: ${invoiceDateStr}`, pageWidth - 15, yPos, { align: 'right' });
  yPos += 5;
  doc.text(`Invoice Number: ${invoice.invoiceNumber}`, pageWidth - 15, yPos, { align: 'right' });
  
  // Line items table
  yPos = Math.max(yPos + 10, 110); // Make sure there's space for table
  
  // Table headers
  doc.setFont(undefined, 'bold');
  doc.text('Item', 15, yPos);
  doc.text('Date', 38, yPos);
  doc.text('Description', 58, yPos);
  doc.text('Quantity', 130, yPos);
  doc.text('Unit Price', 152, yPos);
  doc.text('Amount AUD', pageWidth - 15, yPos, { align: 'right' });
  
  yPos += 2;
  doc.line(15, yPos, pageWidth - 15, yPos);
  yPos += 5;
  
  // Line items
  doc.setFont(undefined, 'normal');
  invoice.lineItems.forEach(item => {
    // Page break with 30mm bottom margin (3cm)
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }
    
    // Item code (smaller column, wrapped if needed)
    const codeLines = doc.splitTextToSize(item.itemCode, 20);
    doc.text(codeLines, 15, yPos);
    
    // Extract date from description if it exists (format: [DD/MM/YYYY])
    let dateStr = '';
    let cleanDescription = item.description;
    const dateMatch = item.description.match(/\[(\d{2}\/\d{2}\/\d{4})\]/);
    if (dateMatch) {
      dateStr = dateMatch[1];
      // Remove date from description
      cleanDescription = item.description.replace(/\[\d{2}\/\d{2}\/\d{4}\]\s*/, '');
    }
    
    // Date column
    if (dateStr) {
      doc.text(dateStr, 38, yPos);
    }
    
    // Description (wrap if needed, give more space)
    const descLines = doc.splitTextToSize(cleanDescription, 66);
    doc.text(descLines, 58, yPos);
    
    // Calculate max lines for row height
    const maxLines = Math.max(codeLines.length, descLines.length);
    
    // Quantity (support both quantity and hours field names)
    const qty = parseFloat(item.quantity ?? item.hours) || 0;
    doc.text(qty.toFixed(2), 130, yPos);
    
    // Unit price with $ (support both unitPrice and rate field names)
    const price = parseFloat(item.unitPrice ?? item.rate) || 0;
    doc.text(`$${price.toFixed(2)}`, 152, yPos);
    
    // Amount with $
    const amt = parseFloat(item.amount) || 0;
    doc.text(`$${amt.toFixed(2)}`, pageWidth - 15, yPos, { align: 'right' });
    
    yPos += (maxLines * 5) + 3;
  });
  
  // Helper: check margin and add page if needed (30mm bottom margin)
  const bottomMargin = 30;
  const checkMargin = (needed = 7) => {
    if (yPos + needed > pageHeight - bottomMargin) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Totals
  yPos += 5;
  checkMargin(20); // need room for subtotal + total block
  doc.line(15, yPos, pageWidth - 15, yPos);
  yPos += 7;
  
  doc.text('Subtotal', 150, yPos);
  doc.text(`$${invoice.subtotal.toFixed(2)}`, pageWidth - 15, yPos, { align: "right" });
  yPos += 7;
  
  doc.setFont(undefined, 'bold');
  doc.text('TOTAL AUD', 150, yPos);
  doc.text(`$${invoice.total.toFixed(2)}`, pageWidth - 15, yPos, { align: "right" });
  
  // Due date
  yPos += 15;
  doc.setFont(undefined, 'normal');
  checkMargin(40); // need room for due date + payment block

  // Safe date formatting for due date
  let dueDateStr = 'Date Not Available';
  try {
    if (invoice.dueDate) {
      const dueDate = invoice.dueDate instanceof Date ? invoice.dueDate : new Date(invoice.dueDate);
      if (!isNaN(dueDate.getTime()) && dueDate.getTime() !== 0) {
        dueDateStr = format(dueDate, 'dd MMM yyyy');
      }
    }
  } catch (error) {
    // Silently handled
  }

  doc.text(`Due Date: ${dueDateStr}`, 15, yPos);

  // Payment details
  yPos += 10;
  checkMargin(25);
  doc.text('Please make payment to:', 15, yPos);
  yPos += 7;
  doc.text(`Account Name: ${settings.bankName}`, 15, yPos);
  yPos += 5;
  doc.text(`BSB: ${settings.bsb}`, 15, yPos);
  yPos += 5;
  doc.text(`Account Number: ${settings.accountNumber}`, 15, yPos);

  // Footer — always at bottom of the last page, inside the bottom margin area
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`ABN: ${settings.abn}. Registered Office: ${settings.address}, ${settings.suburb}, ${settings.state}, ${settings.postcode}, ${settings.country || 'Australia'}.`,
      pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }
  
  return doc;
}
